import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants'
import { CheckClimaEl } from '../../Co2Climate/style'
import { Button, Message, Progress } from 'semantic-ui-react'
import { calculateNitrogenValidation, getStepHeadMessage, preparePayload } from './util'
import { getCropSteps, StepId } from './constants'
import { FormProvider, useForm } from 'react-hook-form'
import CropDetails from './Steps/CropDetails'
import Soil from './Steps/Soil'
import Inputs from './Steps/Inputs'
import FuelEnergy from './Steps/FuelEnergy'
import Irrigation from './Steps/Irrigation'
import Carbon from './Steps/Carbon'
import Transport from './Steps/Transport'
import { CoolFarmCropEl } from './style'
import Step from './Step'
import { CoolFarmCropOptionsType } from './types'
import { Nullable } from '~/types'
import { handleErrors } from '~/utils/helpers'
import { CoolFarmCropResultsType, CoolFarmCropService } from '~/api/services/coolFarmCrop'
import { useNavigate, useParams } from 'react-router-dom'
import { AnsweredContainer, LogoWrapper, SubmitHeader } from '../../Co2Climate/AnswerCO2Climate/style'
import Results from './Steps/Results'
import cloneDeep from 'lodash.clonedeep'
import Dialog from '~/components/Dialog'
import { ThemeContext } from 'styled-components'
import dayjs from 'dayjs'
import { CUSTOM_FERTILIZER_ID } from './Steps/Inputs/Fertilizer'
import notify from '~/components/Notify'

const LAST_STEP_INDEX = 7

export const CoolFarmCropOptionsContext = createContext<CoolFarmCropOptionsType>(null)

const AnswerCrop: React.FC = () => {
  const { t } = useTranslation()
  const stepsRef = useRef<HTMLDivElement>(null)
  const { id, idCrop } = useParams()
  const navigate = useNavigate()
  const { logoTitle } = useContext(ThemeContext)

  const [activeTab, setActiveTab] = useState(0)
  const [isBusy, setIsBusy] = useState(false)
  const [steps, setSteps] = useState(getCropSteps())
  const [progress, setProgress] = useState(0)
  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const [options, setOptions] = useState<Nullable<CoolFarmCropOptionsType>>(null)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [results, setResults] = useState<Nullable<CoolFarmCropResultsType>>(null)

  const methods = useForm({
    'mode': 'onSubmit'
  })

  useEffect(() => {
    if (id && !idCrop) {
      handleCoolFarmInitial()
    } else if (id && idCrop) {
      handleEdit()
    }
    setSteps(getCropSteps())
  }, [t, id, idCrop])

  useEffect(() => {
    if (stepsRef.current && activeTab <= 8) {
      const container = stepsRef.current
      const containerRect = container.getBoundingClientRect()
      const children = Array.from(container.children)
      const targetEl = children[activeTab] as HTMLElement | undefined

      if (targetEl) {
        const targetRect = targetEl.getBoundingClientRect()

        const containerCenter = containerRect.left + containerRect.width / 2
        const stepCenter = targetRect.left + targetRect.width / 2

        const distanceFromCenter = stepCenter - containerCenter
        const maxDistance = containerRect.width / 2

        const progressPercent = Math.max(0, Math.min(100, 50 + (distanceFromCenter / maxDistance) * 50))
        setProgress(progressPercent)
      }
    }
  }, [activeTab, stepsRef])

  async function handleCoolFarmInitial() {
    try {
      const response = await CoolFarmCropService.getCoolFarmCropAnsOptions(id)
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleEdit() {
    try {
      const response = await CoolFarmCropService.getCoolFarmCropAnsOptions(id)
      const responseData = await CoolFarmCropService.editCoolFarmCropAnswers(id, idCrop)
      if(responseData?.data?.fertilizers?.length > 0) {
        responseData.data.fertilizers.forEach(fertilizer => {
          fertilizer.applicationDate = dayjs(fertilizer.applicationDate).format('DD.MM.YYYY')
        })
      }
      methods.reset({
        ...responseData.data,
        hasTreeBiomass: responseData.data?.hasTreeBiomass ? 1 : 0
      })
      setActiveTab(responseData?.data?.step || 0)
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  function renderSteps() {
    return <CoolFarmCropEl.Steps ref={stepsRef}>
      {steps.map((step, index) => {
        return <Step
          step={step}
          index={index}
          key={step.title}
          handleStepClick={() => setActiveTab(index)}
          active={activeTab === index}
          isLoading={activeTab === index && isLoadingResults}
        />
      })}
    </CoolFarmCropEl.Steps>
  }

  function renderActiveComponentStep() {
    const id = steps[activeTab].id
    switch (id) {
      case StepId.CROP:
        return <CropDetails />
      case StepId.SOIL:
        return <Soil />
      case StepId.INPUTS:
        return <Inputs />
      case StepId.FUEL_ENERGY:
        return <FuelEnergy />
      case StepId.IRRIGATION:
        return <Irrigation />
      case StepId.CARBON:
        return <Carbon />
      case StepId.TRANSPORT:
        return <Transport />
      case StepId.RESULTS:
        return <Results results={results} />
    }
  }

  function renderPreviousButton() {
    return <Button
      type='button'
      onClick={() => setActiveTab(activeTab - 1)}
      content={t('Previous')}
    />
  }

  function renderNextButton() {
    return <Button
      type='submit'
      content={t('Continue')}
    />
  }

  async function handleLoadResults() {
    setIsLoadingResults(true)
    try {
      const data = cloneDeep(methods.getValues())
      const payload = preparePayload(data)
      await CoolFarmCropService.updateCoolFarmCropAnswers(id, payload.id, { ...payload, hasTreeBiomass: 0, step: activeTab })
      const response = await CoolFarmCropService.getCropResults(id, payload.id)
      setResults(response.data)
      setActiveTab(activeTab + 1)
    } catch(error) {
      handleErrors(error)
    } finally {
      setIsLoadingResults(false)
    }
  }

  async function handleSaveDraft(data) {
    setIsLoadingResults(true)
    try {
      const payload = preparePayload(data)
      const response = await CoolFarmCropService.saveCoolFarmCropAnswers(id, { ...payload, hasTreeBiomass: 0, step: activeTab + 1 })
      if(response.data && !payload.id) {
        methods.setValue('id', response.data)
      }
      setActiveTab(activeTab + 1)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsLoadingResults(false)
    }
  }

  async function handleUpdateCrop(data) {
    setIsLoadingResults(true)
    try {
      const payload = preparePayload(data)
      await CoolFarmCropService.updateCoolFarmCropAnswers(id, payload.id, { ...payload, hasTreeBiomass: 0, step: activeTab + 1 })
      setActiveTab(activeTab + 1)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsLoadingResults(false)
    }
  }

  function handleValidateInputStep(data, hasCropId) {
    const customFertilizer = data.fertilizers.find(fertilizer => fertilizer.id === CUSTOM_FERTILIZER_ID)
    if(customFertilizer) {
      const isTotalNValid = calculateNitrogenValidation(customFertilizer)
      if(isTotalNValid) {
        if(hasCropId) {
          handleUpdateCrop(data)
          return
        }
        handleSaveDraft(data)
      } else {
        notify(t('The total % N must equal the sum of all 3 %N components below, e.g. total 30% N = 0% nitrate, 15% ammonium, 15% urea.'), 'error')
      }
    } else {

      if(hasCropId) {
        handleUpdateCrop(data)
        return
      }
      handleSaveDraft(data)
    }
  }

  async function onSubmit(data) {
    const isLastStep = activeTab + 1 === LAST_STEP_INDEX
    const isInputStep = steps[activeTab].id === StepId.INPUTS
    const hasCropId = idCrop || data.id
    if (isLastStep) {
      handleLoadResults()
      return
    }
    if (isInputStep) {
      handleValidateInputStep(data, hasCropId)
      return
    }

    if (hasCropId) {
      handleUpdateCrop(data)
    } else {
      handleSaveDraft(data)
    }
  }

  async function handleSubmitCropAnswers() {
    const data = cloneDeep(methods.getValues())
    const payload = preparePayload(data)
    setIsBusy(true)
    try {
      await CoolFarmCropService.storeCoolFarmCropAnswers(id, data.id, { ...payload, step: activeTab })
      setIsSubmitModalOpen(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderAnwseredPercent() {
    return <div style={{ display: 'flex', alignItems: 'anchor-center', gap: 20 }}>
      <AnsweredContainer>
        <Progress value={activeTab + 1} total={steps.length} progress={'ratio'} />
      </AnsweredContainer>
    </div>
  }

  function renderSubmitButton() {
    return <Button
      style={{ minWidth: 225, color: 'white' }}
      type={'button'}
      disabled={Object.keys(methods.formState.errors).length > 0 || isLoadingResults}
      content={t('Complete data collection')}
      onClick={() => handleSubmitCropAnswers()}
    />
  }

  function renderSubmitModal() {
    return <Dialog
      textHeader={''}
      isDialogOpen={isSubmitModalOpen}
      textSubmit={t('Back to home page')}
      hideCloseIcon
      handleSubmit={() => {
        setIsSubmitModalOpen(false)
        navigate(RouteTypes.COOL_FARM)
      }
      }
      textContent={<div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}>
          <SubmitHeader>{t('Thank you for submitting your data.')}</SubmitHeader>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div>{t('You have the option to edit the data during the survey period.')}</div>
          <div>{t('After the end of the survey period, you will receive a detailed PDF document with all analyses and input values.')}</div>
        </div>
        <LogoWrapper style={{ backgroundImage: `url(${logoTitle})` }} />
      </div>}
    />
  }

  return <Layout
    title={t('Cool Farm Crop Tool')}
    segmented
    isBusy={isBusy}
    isCo2Loader
    close={RouteTypes.COOL_FARM}
    actions={renderAnwseredPercent()}
  >
    {renderSubmitModal()}
    <CoolFarmCropEl.StepsContainer>
      <Progress
        percent={progress}
        size='tiny'
        active
        style={{
          position: 'absolute',
          width: '100%',
          top: 23
        }}
      />
      {renderSteps()}
      {<Message info style={{
        borderRadius: '10px',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'fit-content',
        textAlign: 'center',
        marginTop: '2.5em'
      }}>
        <Message.Content>
          <div dangerouslySetInnerHTML={{ __html: getStepHeadMessage(steps[activeTab].id as StepId) }} />
        </Message.Content>
      </Message>}
    </CoolFarmCropEl.StepsContainer>
    <CheckClimaEl.WizzardContentWrapper onSubmit={methods.handleSubmit(onSubmit)}>
      <CheckClimaEl.StyledContent $isFirstStep={activeTab === 0}>
        <CoolFarmCropOptionsContext.Provider value={options as CoolFarmCropOptionsType}>
          <FormProvider {...methods}>
            {options && renderActiveComponentStep()}
          </FormProvider>
        </CoolFarmCropOptionsContext.Provider>
      </CheckClimaEl.StyledContent>
      <CheckClimaEl.ActionsContainer $activeTab={activeTab}>
        {activeTab > 0 && renderPreviousButton()}
        {activeTab < LAST_STEP_INDEX && renderNextButton()}
        {activeTab === LAST_STEP_INDEX && renderSubmitButton()}
      </CheckClimaEl.ActionsContainer>
    </CheckClimaEl.WizzardContentWrapper>
  </Layout>
}
export default AnswerCrop
